import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useQuestionListsforUser,
  useSetDefaultQuestionList,
  useExportQuestionList,
} from "../../Query/useQuestions";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { FormatListBulletedRounded } from "@mui/icons-material";
import { QuestionItem } from "../../Models/question";
import color from "../../Constants/colors";
import HardwareRoundedIcon from "@mui/icons-material/HardwareRounded";
import HardwareOutlinedIcon from "@mui/icons-material/HardwareOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import { useTranslation } from "react-i18next";
import {
  ProfileCard,
  profileBodyCellSx,
  profileButtonSx,
  profileEmptyCellSx,
  profileHeadCellSx,
  profileTableContainerSx,
  profileTableRowSx,
} from "../Profile/ProfileSection";
import { sidebarMenuPaperSx } from "../DashboardMenu/DashboardMenu";

interface QuestionListsTableProps {
  userId: number;
}

const QuestionListsTable: React.FC<QuestionListsTableProps> = ({ userId }) => {
  const { data, isLoading, isError, error } = useQuestionListsforUser(userId);
  const navigate = useNavigate();
  const { t } = useTranslation("userProfilePage");
  const [open, setOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<QuestionItem | null>(null);
  const { colors: workspace } = useWorkspaceColors();
  const setDefaultMutation = useSetDefaultQuestionList();
  const exportMutation = useExportQuestionList();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exportList, setExportList] = useState<QuestionItem | null>(null);
  const openMenu = Boolean(anchorEl);

  const handleClickOpen = (questionList: QuestionItem) => {
    setSelectedList(questionList);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedList(null);
  };

  const handleConfirm = async () => {
    if (selectedList) {
      setDefaultMutation.mutate({
        questionListId: selectedList.id,
        categoryId: selectedList.category.id,
      });
      handleClose();
    }
  };

  const handleExportList = (
    id: number,
    name: string,
    format: "json" | "docx",
  ) => {
    exportMutation.mutate(
      { id, format },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, `${name}.${format}`);
        },
      },
    );
  };

  const handleExportClick = (
    event: React.MouseEvent<HTMLElement>,
    questionList: QuestionItem,
  ) => {
    setAnchorEl(event.currentTarget);
    setExportList(questionList);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
    setExportList(null);
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) return <div>Error: {error.message}</div>;

  const listCount = data?.question_lists?.length ?? 0;

  return (
    <ProfileCard
      title={t("userQuestionLists")}
      subtitle={listCount ? String(listCount) : undefined}
      icon={<FormatListBulletedRounded />}
    >
      <TableContainer sx={profileTableContainerSx()}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={profileHeadCellSx()}>#</TableCell>
              <TableCell sx={profileHeadCellSx()}>{t("name")}</TableCell>
              <TableCell sx={profileHeadCellSx()}>{t("category")}</TableCell>
              <TableCell sx={profileHeadCellSx()}>{t("default")}</TableCell>
              <TableCell sx={profileHeadCellSx()} align="right">
                {t("actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.question_lists?.length ? (
              data.question_lists.map(
                (question_list: QuestionItem, index: number) => (
                  <TableRow key={question_list.id} sx={profileTableRowSx()}>
                    <TableCell sx={profileBodyCellSx()}>{index + 1}</TableCell>
                    <TableCell
                      sx={{
                        ...profileBodyCellSx(),
                        fontWeight: question_list.default ? 700 : 500,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() =>
                        navigate(`/question_list/${question_list.id}`)
                      }
                    >
                      <Tooltip
                        title={
                          question_list.default ? t("defaultListTooltip") : ""
                        }
                      >
                        <span>{question_list.name}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={profileBodyCellSx()}>
                      {question_list.category.name}
                    </TableCell>
                    <TableCell sx={profileBodyCellSx()}>
                      {question_list.default ? (
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: color.green50,
                            color: color.green700,
                          }}
                        >
                          {t("yes")}
                        </Box>
                      ) : (
                        t("no")
                      )}
                    </TableCell>
                    <TableCell sx={profileBodyCellSx()} align="right">
                      <Tooltip title={t("makeDefault")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleClickOpen(question_list)}
                            disabled={question_list.default}
                            sx={{
                              color: color.primary_dark,
                              "&.Mui-disabled": { color: color.accent },
                            }}
                          >
                            {question_list.default ? (
                              <HardwareRoundedIcon fontSize="small" />
                            ) : (
                              <HardwareOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={t("exportQuestionListTooltip", {
                          listName: question_list.name,
                        })}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleExportClick(e, question_list)}
                          sx={{ color: color.primary_dark }}
                        >
                          <IosShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ),
              )
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={profileEmptyCellSx()}>
                  {t("noQuestionLists")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleExportClose}
        slotProps={{
          paper: { sx: { ...sidebarMenuPaperSx(), minWidth: 180 } },
        }}
      >
        <MenuItem
          onClick={() => {
            if (exportList) {
              handleExportList(exportList.id, exportList.name, "json");
            }
            handleExportClose();
          }}
          sx={{ fontSize: 14 }}
        >
          {t("exportAsJson")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (exportList) {
              handleExportList(exportList.id, exportList.name, "docx");
            }
            handleExportClose();
          }}
          sx={{ fontSize: 14 }}
        >
          {t("exportAsDocx")}
        </MenuItem>
      </Menu>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{ color: color.primary_dark, fontSize: 18, fontWeight: 700 }}
        >
          {t("confirmDefault")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: color.gray700, fontSize: 14.5 }}>
            {t("confirmDefaultMessage", {
              listName: selectedList?.name,
              categoryName: selectedList?.category.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleClose} sx={profileButtonSx("outlined")}>
            {t("cancel")}
          </Button>
          <Button onClick={handleConfirm} sx={profileButtonSx("primary")}>
            {t("yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileCard>
  );
};

export default QuestionListsTable;
